import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DateTimeSelector } from "./reminder/DateTimeSelector";
import { ReminderDialogHeader } from "./reminder/ReminderDialogHeader";
import { ReminderDialogFooter } from "./reminder/ReminderDialogFooter";
import { formatTimezone } from "@/lib/utils";

interface ReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: string;
  onScheduled?: () => void;
  initialScheduledFor?: string;
  onDateSelected?: (date: Date) => void;
  images?: string[];
  video?: string | null;
  timezone?: string;
}

export const ReminderDialog = ({
  open,
  onOpenChange,
  content,
  onScheduled,
  initialScheduledFor,
  onDateSelected,
  images = [],
  video,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
}: ReminderDialogProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState("11:00");

  const handleScheduleReminder = async () => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    const [hours, minutes] = selectedTime.split(':');
    const reminderDate = new Date(selectedDate);
    reminderDate.setHours(parseInt(hours), parseInt(minutes));

    if (reminderDate <= new Date()) {
      toast.error("Please select a future time");
      return;
    }

    if (onDateSelected) {
      onDateSelected(reminderDate);
      onOpenChange(false);
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      toast.error("No user found");
      return;
    }

    try {
      const formattedContent = content;
      
      // Get video size if video exists
      let videoData;
      if (video) {
        const response = await fetch(video);
        if (!response.ok) {
          throw new Error('Failed to fetch video data');
        }
        const blob = await response.blob();
        videoData = {
          url: video,
          size: blob.size
        };
      }
      
      const { error: dbError } = await supabase
        .from('scheduled_posts')
        .insert({
          content: formattedContent,
          scheduled_for: reminderDate.toISOString(),
          timezone: timezone,
          user_id: user.id,
          images: images,
          video: video
        });

      if (dbError) throw dbError;

      toast.success("Post scheduled!");
      onScheduled?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Error scheduling post:", error);
      toast.error("Failed to schedule post");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <ReminderDialogHeader />
        <div className="space-y-4 py-2">
          <DateTimeSelector
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
            initialScheduledFor={initialScheduledFor}
            timezone={timezone}
          />
        </div>
        <ReminderDialogFooter
          onCancel={() => onOpenChange(false)}
          onSchedule={handleScheduleReminder}
        />
      </DialogContent>
    </Dialog>
  );
};